import React from 'react';
import { Bar, XAxis, Tooltip as RechartsTooltip, CartesianGrid, YAxis, ComposedChart, Line } from 'recharts';
import { ReloadIconButton, MedicalSupplySelectModal, WeekRangeSelector, Title } from '../../elements';
import { MedicalSupplyViewModel } from '../../models/injection';
import { extractApiErrorMessage, notifications, colors } from '../../utils';
import { ApiService } from '../../hooks';
import {
  HealthSystemAPIControllersInjectionsResponsesInjectionHistoryLocationStrategyResponse,
  InjectionsGetLocationInjectionStrategyParams
} from '../../api/HealthSystemApi';
import { Grid, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import VaccinationAndMedicationDataTable from '../VaccinationAndMedicationDataTable';
import { Card, CardHeader, CardContent } from '../../elements/ui/card';
import { Skeleton } from '../../elements/ui/skeleton';
import { ChartContainer, ChartLegend, ChartLegendContent, type ChartConfig } from '../../elements/ui/chart';
import { getISOWeekNumber } from '../../utils/date-range';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';

const getDefaultWeekRange = () => {
  const now = new Date();
  const currentWeek = getISOWeekNumber(now);
  return {
    start: Math.max(1, currentWeek - 10),
    end: currentWeek
  };
};

interface LocationVaccinationAndMedicationProps {
  locationId: string;
  onHasData?: (hasData: boolean) => void;
}

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  const totalAnimals = data?.animalCount ?? 0;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">Week {label}</p>
      {payload
        .filter((entry: any) => entry.name?.trim())
        .map((entry: any) => {
          const displayValue = entry.dataKey === 'liveCount' ? totalAnimals : entry.value;
          return (
            <div key={entry.name} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.fill || entry.color }} />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">{displayValue}</span>
              {entry.dataKey === 'diedAnimalCount' && totalAnimals > 0 && (
                <span className="text-gray-500">({((entry.value / totalAnimals) * 100).toFixed(1)}%)</span>
              )}
            </div>
          );
        })}
    </div>
  );
}

const LocationVaccinationAndMedication: React.FC<LocationVaccinationAndMedicationProps> = ({
  locationId,
  onHasData
}) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const currentYear = new Date().getFullYear();
  const defaultWeekRange = getDefaultWeekRange();

  const [year, setYear] = React.useState(currentYear);
  const [weekStart, setWeekStart] = React.useState(defaultWeekRange.start);
  const [weekEnd, setWeekEnd] = React.useState(defaultWeekRange.end);
  const [selectedMedicalSupply, setSelectedMedicalSupply] = React.useState<MedicalSupplyViewModel | null>(null);

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [data, setData] =
    React.useState<HealthSystemAPIControllersInjectionsResponsesInjectionHistoryLocationStrategyResponse | null>(null);

  const trendChartData = React.useMemo(() => (data?.trendData ?? []).map((item) => ({ ...item })), [data]);

  const trendChartConfig = React.useMemo<ChartConfig>(
    () => ({
      animalCount: { label: t('f-strategy-trend-treated-num'), color: '#413ea0' }
    }),
    [t]
  );

  const loadData = async () => {
    setIsBusy(true);
    setLoaded(false);

    apiService.api.injections
      .injectionsGetLocationInjectionStrategy({
        locationId,
        Year: year,
        'Week.Start': weekStart,
        'Week.End': weekEnd,
        SupplyId: selectedMedicalSupply?.id ?? undefined
      } as InjectionsGetLocationInjectionStrategyParams)
      .then((result) => {
        setData(result.data);
        const hasData = (result.data.supplyInfo?.length ?? 0) > 0 || (result.data.trendData?.length ?? 0) > 0;
        onHasData?.(hasData);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  React.useEffect(() => {
    if (!loaded) {
      loadData();
    }
  }, [loaded]);

  const handleWeekRangeChange = (start: number, end: number) => {
    setWeekStart(start);
    setWeekEnd(end);
    setLoaded(false);
  };

  const handleMedicalSupplyChange = (supply: MedicalSupplyViewModel | null) => {
    setSelectedMedicalSupply(supply);
    setLoaded(false);
  };

  return (
    <Grid container spacing={3}>
      {/* Vaccination & Medication section */}
      <Grid item xs={12}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item container xs={10} spacing={1}>
            <Grid item>
              <LocalHospitalIcon color="primary" fontSize="large" />
            </Grid>
            <Grid item>
              <Title>{t('f-strat-vacc-title')}</Title>
            </Grid>
          </Grid>
          <Grid item xs={1}>
            <ReloadIconButton action={() => setLoaded(false)} />
          </Grid>
        </Grid>

        {/* Filters */}
        <Grid item container alignItems="center" spacing={1} style={{ marginTop: 8 }}>
          <Grid item>
            <TextField
              variant="outlined"
              type="number"
              label={t('f-com-year')}
              size="small"
              value={year}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (newValue >= currentYear - 20 && newValue <= currentYear) {
                  setYear(newValue);
                  setLoaded(false);
                }
              }}
            />
          </Grid>

          <Grid item xs={2} sm={2} md={2}>
            <MedicalSupplySelectModal
              fullWidth={true}
              supply={selectedMedicalSupply}
              onChange={handleMedicalSupplyChange}
            />
          </Grid>

          <Grid item>
            <WeekRangeSelector onChange={handleWeekRangeChange} weeksBack={10} year={year} className="min-w-70" />
          </Grid>
        </Grid>

        {/* Table skeleton */}
        {isBusy && (
          <Grid item xs={12} style={{ marginTop: 8 }}>
            <div className="rounded border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex gap-4">
                {[1, 2, 3, 4, 5].map((_, i) => (
                  <Skeleton key={i} className="h-4 flex-1" />
                ))}
              </div>
              {[1, 2, 3, 4, 5].map((row) => (
                <div key={row} className="flex gap-4 px-4 py-3 border-b border-gray-100 last:border-0">
                  {[1, 2, 3, 4, 5].map((_, i) => (
                    <Skeleton key={i} className="h-3.5 flex-1" />
                  ))}
                </div>
              ))}
            </div>
          </Grid>
        )}

        {!isBusy && loaded && data && (
          <Grid item xs={12} style={{ marginTop: 8 }}>
            <VaccinationAndMedicationDataTable supplyInfo={data.supplyInfo ?? []} showPerAnimalColumns={false} />
          </Grid>
        )}
      </Grid>

      {/* Trend Data section */}
      <Grid item xs={12}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUpIcon color="primary" />
            <h2 className="text-lg font-semibold text-gray-800">{t('f-strategy-trend')}</h2>
          </div>
        </div>

        {isBusy && (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="p-6">
              <div className="flex items-end gap-3 h-62.5">
                {[55, 75, 45, 85, 65, 90, 50, 70, 80, 60, 40, 72].map((h, i) => (
                  <Skeleton key={i} className="flex-1 rounded-t-sm rounded-b-none" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {loaded && !isBusy && (
          <Card>
            <CardHeader className="pb-2 min-h-13">
              <h3 className="text-sm font-medium text-gray-700">{t('f-strategy-trend')}</h3>
            </CardHeader>
            <CardContent>
              <div className="w-full aspect-2/1 min-h-62.5 max-h-100">
                <ChartContainer config={trendChartConfig} className="w-full h-full">
                  <ComposedChart data={trendChartData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="weekNumber"
                      padding={{ left: 30, right: 30 }}
                      scale="band"
                      tickLine={false}
                      tickMargin={10}
                      axisLine={false}
                    />
                    <YAxis axisLine={false} tickLine={false} width={40} />
                    <RechartsTooltip content={<TrendTooltip />} cursor={false} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      name={t('f-strategy-trend-treated-num')}
                      dataKey="animalCount"
                      stackId="a"
                      fill="var(--color-animalCount)"
                      radius={[4, 4, 4, 4]}
                    />
                    <Line
                      name=" "
                      type="monotone"
                      dataKey="animalCount"
                      stroke={colors.barChartLine}
                      dot={false}
                      legendType="none"
                    />
                  </ComposedChart>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};

export default LocationVaccinationAndMedication;
